import React, { useEffect, useRef, useState } from 'react'

import { FmClearIcon } from '@octane/spark'
import getModels, { GetModelsOptions, Model } from 'api/vehicles/getModels'
import { FormikDropdown, FormikTextInput } from 'components/FormikFields'
import { FormikDropdownChild } from 'components/FormikFields/FormikDropdown/FormikDropdown'
import { FieldKit } from 'containers/FormBuilder'
import { FormikValues, getIn } from 'formik'
import AssetType from 'state/enums/AssetType'

import {
    CancelManualEntryButton,
    ManualEntryButton,
    TextInputWrapper,
} from './dropdowns.styled'

const AUTOMOBILE_WRITE_IN = 'Automobile Write-in'

interface ModelDropdownProps {
    formik: FieldKit<FormikValues>
    assetType: AssetType
    dataSource?: string[]
    prefilled?: boolean
    onModelChanged?: () => void
    onTextInputMode?: (active: boolean) => void
}

const defaultProps = {
    dataSource: null,
    prefilled: false,
    onModelChanged: undefined,
    onTextInputMode: undefined,
}

export default function ModelDropdown({
    formik,
    assetType,
    dataSource,
    prefilled,
    onModelChanged,
    onTextInputMode,
}: Readonly<ModelDropdownProps>) {
    const [response, setResponse] = useState([] as Model[])
    const [loading, setLoading] = useState(false)
    const [manualEntry, setManualEntry] = useState(false)
    const [hasFetched, setHasFetched] = useState(false)
    const hasLoadedRef = useRef(false)
    const [refreshKey, setRefreshKey] = useState(0)
    const { vehicle } = formik.values
    const { condition, year, makeId, modelId } = vehicle

    function cancelManualEntry() {
        formik.setFieldValue('vehicle.model', '')
        formik.setFieldValue('vehicle.modelId', '')
        formik.setFieldValue('vehicle.automobileId', '')
        formik.setFieldValue('vehicle.trim', '')
        onModelChanged?.()
        setRefreshKey((k) => k + 1)
    }

    const isTextInput =
        (hasFetched && !loading && response.length === 0) || manualEntry

    useEffect(() => {
        if (makeId && year) {
            onTextInputMode?.(isTextInput)
        } else {
            onTextInputMode?.(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, response.length, makeId, year, isTextInput])

    useEffect(() => {
        const abortController = new AbortController()

        setManualEntry(false)
        setHasFetched(false)

        if (condition && year && makeId) {
            const getModelsOptions = {
                condition,
                year,
                makeId,
                dataSource,
                assetType,
            } as GetModelsOptions

            setResponse([])
            setLoading(true)

            getModels(getModelsOptions, abortController)
                .then((models: Model[]) => {
                    const filtered = models.filter(
                        (m) => m.name !== AUTOMOBILE_WRITE_IN,
                    )

                    setResponse(filtered)
                    setLoading(false)
                    setHasFetched(true)

                    const selectedModel = filtered.find(
                        (m) => m.id.toString() === modelId,
                    )

                    if (selectedModel) {
                        formik.setFieldValue(
                            'vehicle.model',
                            selectedModel.name,
                        )
                    } else {
                        formik.setFieldValue('vehicle.modelId', '')
                        if (vehicle.model && !hasLoadedRef.current) {
                            formik.setFieldValue('vehicle.automobileId', '')
                            setManualEntry(true)
                        } else {
                            formik.setFieldValue('vehicle.model', '')
                            formik.setFieldValue('vehicle.automobileId', '')
                            formik.setFieldValue('vehicle.trim', '')
                            onModelChanged?.()
                        }
                    }

                    hasLoadedRef.current = true
                })
                .catch(() => {
                    setLoading(false)
                })
        }

        return () => abortController.abort()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [condition, year, makeId, refreshKey])

    useEffect(() => {
        const selectedModel = response.find((m) => m.id.toString() === modelId)

        if (selectedModel) {
            formik.setFieldValue('vehicle.model', selectedModel.name)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modelId])

    if (!makeId || !year) {
        return (
            <FormikDropdown
                name="vehicle.modelId"
                label="Model"
                placeholder="Select One"
                disabled
                prefilled={prefilled}
            >
                {[{ name: '', value: '' }]}
            </FormikDropdown>
        )
    }

    if (isTextInput) {
        return (
            <TextInputWrapper>
                <FormikTextInput
                    name="vehicle.model"
                    label="Model"
                    prefilled={prefilled}
                />
                {manualEntry &&
                    !(
                        getIn(formik.errors, 'vehicle.model') &&
                        getIn(formik.touched, 'vehicle.model')
                    ) && (
                        <CancelManualEntryButton
                            type="button"
                            aria-label="Cancel manual entry"
                            onClick={cancelManualEntry}
                        >
                            <FmClearIcon height={24} />
                        </CancelManualEntryButton>
                    )}
            </TextInputWrapper>
        )
    }

    return (
        <FormikDropdown
            name="vehicle.modelId"
            label="Model"
            placeholder="Select One"
            disabled={loading || response.length === 0}
            error={!loading && (modelId === null || modelId === '')}
            searchable
            searchPlaceholder="Search models..."
            prefilled={prefilled}
            noMatchComponent={
                <ManualEntryButton
                    type="button"
                    onClick={() => {
                        formik.setFieldValue('vehicle.modelId', '')
                        formik.setFieldValue('vehicle.model', '')
                        formik.setFieldValue('vehicle.automobileId', '')
                        formik.setFieldValue('vehicle.trim', '')
                        onModelChanged?.()
                        setManualEntry(true)
                    }}
                >
                    Add model manually
                </ManualEntryButton>
            }
            onChange={() => {
                formik.setFieldValue('vehicle.automobileId', '')
                formik.setFieldValue('vehicle.trim', '')
                onModelChanged?.()
            }}
        >
            {response.map(
                (child: Model): FormikDropdownChild => ({
                    name: child.name,
                    value: child.id.toString(),
                }),
            )}
        </FormikDropdown>
    )
}

ModelDropdown.defaultProps = defaultProps
